import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Link, NavLink, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Loader from '../../components/Loader/Loader';
import Error from '../../components/Error/Error';
import * as elementsActions from '../../redux/actions/elements';
import { getNewsMedia } from '../../shared/repos/graphql/news';
import endpoints from '../../shared/constants/endpoints';
import {
  MEDIA_SECTIONS, NEWSROOM_TITLE, getMediaSection, mediaPath,
} from './helpers';
import styles from './News.module.css';

const exitIcon = require('../../components/Modal/imgs/exit-icon.svg');

const downloadClass = 'font-montserratBold underline';

const VideoGallery = ({ items }) => {
  const [selectedId, setSelectedId] = useState(items[0]?.id);
  const selected = items.find(item => item.id === selectedId) || items[0];

  return (
    <div className="flex flex-col gap-8">
      <div>
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <video
          key={selected.id}
          src={selected.fileUrl}
          poster={selected.thumbnailUrl || undefined}
          controls
          preload="metadata"
          className="w-full rounded-xl bg-darkBlack"
        />
        <div className="flex flex-wrap items-center justify-between gap-4 mt-4">
          <h2 className="font-montserratBold text-xl">{selected.title}</h2>
          {selected.downloadUrl && <a href={selected.downloadUrl} className={downloadClass}>Download video</a>}
        </div>
        {selected.description && <p className="mt-2 font-montserratMedium">{selected.description}</p>}
      </div>
      {items.length > 1 && (
        <ul className={styles.thumbGrid} data-part="news-grid">
          {items.map(item => (
            <li key={item.id}>
              <button
                type="button"
                data-part="news-thumb"
                onClick={() => setSelectedId(item.id)}
                aria-pressed={item.id === selected.id}
                className={`${styles.thumbButton} ${item.id === selected.id ? styles.thumbActive : ''} w-full text-left flex flex-col gap-2`}
              >
                <div className={`${styles.frame} ${styles.ratio16x9}`}>
                  {item.thumbnailUrl && <img src={item.thumbnailUrl} alt="" loading="lazy" className="w-full h-full object-cover" />}
                </div>
                <span className="font-montserratMedium">{item.title}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const ImageGallery = ({ items, onOpen }) => (
  <ul className={styles.imageGrid} data-part="news-grid">
    {items.map(item => (
      <li key={item.id} className="flex flex-col gap-2">
        <button
          type="button"
          onClick={() => onOpen(item)}
          className={`${styles.frame} ${styles.ratio4x3} block w-full`}
          aria-label={`View ${item.title || 'image'}`}
        >
          <img
            src={item.thumbnailUrl}
            alt={item.title || ''}
            loading="lazy"
            className="w-full h-full object-contain p-4"
          />
        </button>
        {item.title && <span className="font-montserratMedium">{item.title}</span>}
        {item.downloadUrl && <a href={item.downloadUrl} className={`${downloadClass} text-sm`}>Download</a>}
      </li>
    ))}
  </ul>
);

const NewsMedia = ({ setModalObject }) => {
  const { type } = useParams();
  const section = getMediaSection(type);
  const [state, setState] = useState({ type: null, error: false, items: [] });

  useEffect(() => {
    if (!section) return undefined;
    let cancelled = false;
    getNewsMedia(section.type)
      .then(({ data }) => {
        if (!cancelled) setState({ type: section.type, error: false, items: data.newsMedia || [] });
      })
      .catch(() => {
        if (!cancelled) setState({ type: section.type, error: true, items: [] });
      });
    return () => { cancelled = true; };
  }, [section]);

  const loaded = section && state.type === section.type;

  if (!section || (loaded && state.error)) {
    return (
      <>
        <Helmet><title>{NEWSROOM_TITLE}</title></Helmet>
        <Error errorType={section ? '400' : '404'} />
      </>
    );
  }

  const openImage = item => {
    setModalObject({
      children: (
        <div className="relative w-full">
          <button
            type="button"
            onClick={() => setModalObject(null)}
            className="absolute top-4 right-4 z-20 w-6 h-6 bg-transparent border-none p-0"
            aria-label="Close"
          >
            <img alt="" src={exitIcon} className="w-full h-full" />
          </button>
          <div className="p-5 flex flex-col items-center gap-4">
            <img src={item.fileUrl} alt={item.title || ''} className={styles.lightboxImage} />
            {item.downloadUrl && <a href={item.downloadUrl} className={`${downloadClass} text-dark`}>Download</a>}
          </div>
        </div>
      ),
    });
  };

  return (
    <div className={styles.pageWrapper} data-region="secondary-page" data-page="NewsMedia">
      <Helmet>
        <title>{`${section.title} | Insomnia Cookies Newsroom`}</title>
        <meta name="description" content={`Insomnia Cookies press ${section.title.toLowerCase()} for media use.`} />
      </Helmet>
      <div className={`${styles.pageContainer} text-dark dark:text-white`} data-part="content-surface">
        <Link to={endpoints.news} className="inline-block mb-8 font-montserratBold hover:underline" data-part="news-back">
          ← Newsroom
        </Link>
        <h1 className={styles.pageTitle}>{section.title}</h1>
        <nav aria-label="Multimedia sections" className="flex flex-wrap gap-3 mb-10">
          {MEDIA_SECTIONS.map(item => (
            <NavLink
              key={item.slug}
              to={mediaPath(item.slug)}
              data-part="news-pill"
              className="px-5 py-2 rounded-full border-2 border-dark dark:border-white font-montserratBold"
              activeClassName="bg-dark text-white"
            >
              {item.title}
            </NavLink>
          ))}
        </nav>
        {!loaded && <Loader id="loader" />}
        {loaded && state.items.length === 0 && (
          <p className="font-montserratMedium text-lg">Nothing here yet. Check back soon.</p>
        )}
        {loaded && state.items.length > 0 && (
          section.type === 'VIDEO'
            ? <VideoGallery items={state.items} />
            : <ImageGallery items={state.items} onOpen={openImage} />
        )}
      </div>
    </div>
  );
};

const mediaItemShape = PropTypes.shape({
  id: PropTypes.string.isRequired,
  title: PropTypes.string,
  description: PropTypes.string,
  thumbnailUrl: PropTypes.string,
  fileUrl: PropTypes.string,
  downloadUrl: PropTypes.string,
});

VideoGallery.propTypes = { items: PropTypes.arrayOf(mediaItemShape).isRequired };
ImageGallery.propTypes = {
  items: PropTypes.arrayOf(mediaItemShape).isRequired,
  onOpen: PropTypes.func.isRequired,
};
NewsMedia.propTypes = { setModalObject: PropTypes.func.isRequired };

export const mapDispatchToProps = dispatch => ({
  setModalObject: value => dispatch(elementsActions.setModalObject(value)),
});

export default connect(null, mapDispatchToProps)(NewsMedia);
